import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Slider,
  Paper,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useWizard } from '../../context/WizardContext';
import { useProgressTracking } from '../../hooks/useProgressTracking';
import CurrencyInput from '../common/CurrencyInput';

// Qualified/non-qualified allocation lookup (mirrors server-side BASIC_MODE_DEFAULTS.assetAllocation)
const QUALIFIED_PCT: Record<string, Record<string, number>> = {
  UNDER_50:    { UNDER_1M: 85, T_1M_2M: 79, T_2M_3M: 64, T_3M_5M: 55, T_5M_10M: 34, T_10M_PLUS: 25 },
  AGE_50_59:   { UNDER_1M: 85, T_1M_2M: 72, T_2M_3M: 65, T_3M_5M: 53, T_5M_10M: 36, T_10M_PLUS: 26 },
  AGE_60_69:   { UNDER_1M: 85, T_1M_2M: 68, T_2M_3M: 60, T_3M_5M: 51, T_5M_10M: 36, T_10M_PLUS: 26 },
  AGE_70_PLUS: { UNDER_1M: 85, T_1M_2M: 48, T_2M_3M: 46, T_3M_5M: 38, T_5M_10M: 24, T_10M_PLUS: 18 },
};
const MAX_QUALIFIED: Record<string, number> = {
  UNDER_50: 2200000, AGE_50_59: 3200000, AGE_60_69: 3800000, AGE_70_PLUS: 3000000,
};

const calculateAge = (dob: string): number => {
  if (!dob) return 0;
  const currentYear = new Date().getFullYear();
  const birthYear = parseInt(dob.substring(0, 4), 10);
  if (isNaN(birthYear)) return 0;
  return currentYear - birthYear;
};

const getDefaultQualifiedPct = (totalSavings: number, oldestAge: number): number => {
  if (totalSavings <= 0 || oldestAge <= 0) return 60; // fallback
  const ageGroup = oldestAge < 50 ? 'UNDER_50' : oldestAge < 60 ? 'AGE_50_59' : oldestAge < 70 ? 'AGE_60_69' : 'AGE_70_PLUS';
  const tier = totalSavings < 1000000 ? 'UNDER_1M' : totalSavings <= 2000000 ? 'T_1M_2M' : totalSavings <= 3000000 ? 'T_2M_3M' : totalSavings <= 5000000 ? 'T_3M_5M' : totalSavings < 10000000 ? 'T_5M_10M' : 'T_10M_PLUS';
  const pct = QUALIFIED_PCT[ageGroup][tier];
  const maxQ = MAX_QUALIFIED[ageGroup];
  const qualifiedRaw = Math.round(totalSavings * (pct / 100));
  if (qualifiedRaw > maxQ) {
    return Math.round((maxQ / totalSavings) * 100);
  }
  return pct;
};

const BasicOptionalStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  useProgressTracking({ stepName: 'Optional Details', stepNumber: 3 });
  const { personal, employment, socialSecurity, savings } = wizardData;

  const hasPartner = personal.hasPartner || false;
  const totalAssets = wizardData.totalAssets || 0;

  // Derive initial allocation from existing savings data or use lookup-table defaults
  const getInitialAllocation = useCallback(() => {
    const tdTotal = savings.taxDeferred.reduce((s, a) => s + a.balance, 0);
    const tfTotal = savings.taxFree.reduce((s, a) => s + a.balance, 0);
    const txTotal = savings.taxable.reduce((s, a) => s + a.balance, 0);
    const sum = tdTotal + tfTotal + txTotal;
    if (sum > 0 && totalAssets > 0) {
      return {
        taxDeferred: Math.round((tdTotal / totalAssets) * 100),
        taxFree: Math.round((tfTotal / totalAssets) * 100),
        taxable: Math.round((txTotal / totalAssets) * 100)
      };
    }
    const client1Age = calculateAge(personal.client1.dateOfBirth);
    const client2Age = hasPartner ? calculateAge(personal.client2.dateOfBirth) : 0;
    const oldestAge = Math.max(client1Age, client2Age);
    const qualifiedPct = getDefaultQualifiedPct(totalAssets, oldestAge);
    return { taxDeferred: qualifiedPct, taxFree: 0, taxable: 100 - qualifiedPct };
  }, [savings, totalAssets, personal, hasPartner]);

  const [allocation, setAllocation] = useState(getInitialAllocation);
  const [showBreakdown, setShowBreakdown] = useState(
    savings.taxDeferred.length > 0 || savings.taxFree.length > 0 || savings.taxable.length > 0
  );

  const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  const updateSavingsFromAllocation = (newAllocation: typeof allocation) => {
    const td = Math.round(totalAssets * (newAllocation.taxDeferred / 100));
    const tf = Math.round(totalAssets * (newAllocation.taxFree / 100));
    const tx = totalAssets - td - tf; // Remainder to avoid rounding issues

    updateWizardData({
      savings: {
        taxDeferred: td > 0 ? [{ accountName: 'Tax-Deferred Accounts', balance: td, owner: 'client1' as const }] : [],
        taxFree: tf > 0 ? [{ accountName: 'Tax-Free Accounts', balance: tf, owner: 'client1' as const }] : [],
        taxable: tx > 0 ? [{ accountName: 'Taxable Accounts', balance: tx, owner: 'client1' as const }] : []
      }
    });
  };

  const handleSliderChange = (type: 'taxDeferred' | 'taxFree' | 'taxable', newValue: number) => {
    const otherKeys = (['taxDeferred', 'taxFree', 'taxable'] as const).filter(k => k !== type);
    const otherSum = otherKeys.reduce((s, k) => s + allocation[k], 0);

    const clamped = Math.min(newValue, 100);
    const remaining = 100 - clamped;

    let newAllocation: typeof allocation;
    if (otherSum === 0) {
      newAllocation = {
        ...allocation,
        [type]: clamped,
        [otherKeys[0]]: Math.round(remaining / 2),
        [otherKeys[1]]: remaining - Math.round(remaining / 2)
      };
    } else {
      const ratio0 = allocation[otherKeys[0]] / otherSum;
      const val0 = Math.round(remaining * ratio0);
      const val1 = remaining - val0;
      newAllocation = {
        ...allocation,
        [type]: clamped,
        [otherKeys[0]]: val0,
        [otherKeys[1]]: val1
      };
    }

    setAllocation(newAllocation);
    updateSavingsFromAllocation(newAllocation);
  };

  const handleBreakdownToggle = (expanded: boolean) => {
    setShowBreakdown(expanded);
    if (expanded) {
      updateSavingsFromAllocation(allocation);
    } else {
      updateWizardData({
        savings: { taxDeferred: [], taxFree: [], taxable: [] }
      });
    }
  };

  // Social Security handlers - client1
  const handleSSCurrentAmount = (value: number) => {
    updateWizardData({
      socialSecurity: {
        ...socialSecurity,
        client1: { ...socialSecurity.client1, currentAmount: value }
      }
    });
  };

  const handleSSProjectedAmount = (value: number) => {
    updateWizardData({
      socialSecurity: {
        ...socialSecurity,
        client1: { ...socialSecurity.client1, projectedAmount: value }
      }
    });
  };

  // Social Security handlers - client2 (partner)
  const handlePartnerSSCurrentAmount = (value: number) => {
    updateWizardData({
      socialSecurity: {
        ...socialSecurity,
        client2: { ...socialSecurity.client2, currentAmount: value }
      }
    });
  };

  const handlePartnerSSProjectedAmount = (value: number) => {
    updateWizardData({
      socialSecurity: {
        ...socialSecurity,
        client2: { ...socialSecurity.client2, projectedAmount: value }
      }
    });
  };

  // Pension handlers - client1
  const handlePensionToggle = (checked: boolean) => {
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          hasPension: checked,
          pensionStartAge: checked ? (employment.client1.pensionStartAge || 67) : undefined,
          pensionBenefitAmount: checked ? (employment.client1.pensionBenefitAmount || 0) : undefined
        }
      }
    });
  };

  // Pension handlers - client2 (partner)
  const handlePartnerPensionToggle = (checked: boolean) => {
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          hasPension: checked,
          pensionStartAge: checked ? (employment.client2.pensionStartAge || 67) : undefined,
          pensionBenefitAmount: checked ? (employment.client2.pensionBenefitAmount || 0) : undefined
        }
      }
    });
  };

  const sliderItems: { key: 'taxDeferred' | 'taxFree' | 'taxable'; label: string; description: string }[] = [
    { key: 'taxDeferred', label: 'Tax-Deferred', description: '401(k), Traditional IRA, etc.' },
    { key: 'taxFree', label: 'Tax-Free', description: 'Roth IRA, Roth 401(k), etc.' },
    { key: 'taxable', label: 'Taxable', description: 'Brokerage, Savings, etc.' },
  ];

  const partnerFirstName = personal.client2.firstName || 'Partner';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" textAlign="center">
        Additional Details
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        These are optional but help us provide a more accurate analysis.
      </Typography>

      {/* Social Security */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Know your Social Security details?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={wizardData.uiState.isClaimingSocialSecurity || false}
                  onChange={(e) => {
                    updateWizardData({
                      uiState: {
                        ...wizardData.uiState,
                        isClaimingSocialSecurity: e.target.checked,
                        hasAnsweredSocialSecurityQuestion: true
                      }
                    });
                  }}
                />
              }
              label="I'm currently receiving Social Security"
            />
            {wizardData.uiState.isClaimingSocialSecurity ? (
              <CurrencyInput
                label="Current Annual SS Benefit"
                value={socialSecurity.client1.currentAmount || ''}
                onChange={handleSSCurrentAmount}
                helperText="Your current annual Social Security benefit"
                fullWidth
              />
            ) : (
              <CurrencyInput
                label="Estimated Annual SS Benefit at 67"
                value={socialSecurity.client1.projectedAmount || ''}
                onChange={handleSSProjectedAmount}
                helperText="If you're not sure, we'll estimate it for you"
                fullWidth
              />
            )}

            {/* Partner Social Security */}
            {hasPartner && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight="medium">
                  {partnerFirstName}'s Social Security
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={socialSecurity.partnerIsClaiming || false}
                      onChange={(e) => {
                        updateWizardData({
                          socialSecurity: {
                            ...socialSecurity,
                            partnerIsClaiming: e.target.checked
                          },
                          uiState: {
                            ...wizardData.uiState,
                            hasAnsweredPartnerSocialSecurityQuestion: true
                          }
                        });
                      }}
                    />
                  }
                  label={`${partnerFirstName} is currently receiving Social Security`}
                />
                {socialSecurity.partnerIsClaiming ? (
                  <CurrencyInput
                    label={`${partnerFirstName}'s Current Annual SS Benefit`}
                    value={socialSecurity.client2.currentAmount || ''}
                    onChange={handlePartnerSSCurrentAmount}
                    helperText={`${partnerFirstName}'s current annual Social Security benefit`}
                    fullWidth
                  />
                ) : (
                  <CurrencyInput
                    label={`${partnerFirstName}'s Estimated Annual SS Benefit at 67`}
                    value={socialSecurity.client2.projectedAmount || ''}
                    onChange={handlePartnerSSProjectedAmount}
                    helperText="If you're not sure, we'll estimate it"
                    fullWidth
                  />
                )}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Savings Breakdown */}
      <Accordion
        expanded={showBreakdown}
        onChange={(_, expanded) => handleBreakdownToggle(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Want to break down your savings?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {totalAssets > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Adjust the sliders to show how your {formatCurrency(totalAssets)} is distributed across account types.
                </Typography>
                {sliderItems.map(({ key, label, description }) => (
                  <Box key={key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(Math.round(totalAssets * (allocation[key] / 100)))} ({allocation[key]}%)
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {description}
                    </Typography>
                    <Slider
                      value={allocation[key]}
                      min={0}
                      max={100}
                      onChange={(_, val) => handleSliderChange(key, val as number)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}%`}
                    />
                  </Box>
                ))}
                <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Total: {allocation.taxDeferred + allocation.taxFree + allocation.taxable}% of {formatCurrency(totalAssets)}
                  </Typography>
                </Paper>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Please enter your total savings on the previous step first.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pension */}
      <Accordion
        expanded={employment.client1.hasPension || false}
        onChange={(_, expanded) => handlePensionToggle(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Have a pension?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                When does your pension start? (Age: {employment.client1.pensionStartAge || 67})
              </Typography>
              <Slider
                value={employment.client1.pensionStartAge || 67}
                min={50}
                max={85}
                onChange={(_, val) => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client1: { ...employment.client1, pensionStartAge: val as number }
                    }
                  });
                }}
              />
            </Box>
            <CurrencyInput
              label="Annual Pension Benefit"
              value={employment.client1.pensionBenefitAmount || ''}
              onChange={(value) => {
                updateWizardData({
                  employment: {
                    ...employment,
                    client1: { ...employment.client1, pensionBenefitAmount: value }
                  }
                });
              }}
              fullWidth
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Partner Pension */}
      {hasPartner && (
        <Accordion
          expanded={employment.client2.hasPension || false}
          onChange={(_, expanded) => handlePartnerPensionToggle(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Does {partnerFirstName} have a pension?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  When does {partnerFirstName}'s pension start? (Age: {employment.client2.pensionStartAge || 67})
                </Typography>
                <Slider
                  value={employment.client2.pensionStartAge || 67}
                  min={50}
                  max={85}
                  onChange={(_, val) => {
                    updateWizardData({
                      employment: {
                        ...employment,
                        client2: { ...employment.client2, pensionStartAge: val as number }
                      }
                    });
                  }}
                />
              </Box>
              <CurrencyInput
                label={`${partnerFirstName}'s Annual Pension Benefit`}
                value={employment.client2.pensionBenefitAmount || ''}
                onChange={(value) => {
                  updateWizardData({
                    employment: {
                      ...employment,
                      client2: { ...employment.client2, pensionBenefitAmount: value }
                    }
                  });
                }}
                fullWidth
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default BasicOptionalStep;
